import classNames from "classnames";
import { useRef } from "react";
import { ActiveEntityType, ActiveOrder, LineItem } from "../../build/activeEntityType";
import { NodeStates } from "../../build/types";
import { useInteractionStates } from "../../hooks/useInteractionStates";
import { findInChildren } from "../../utils/cloneNode";
import { OrderDataComponentProps } from "../BuilderInterface";
import { getCompoundStyle, getStateStyleForBuilder, getStylesFromInteractiveStates } from "../helpers";
import SingleLineItem from "./SingleLineItem";
import "./style.scss";
import { DynamicDataSectionProvider } from "../../Contexts/DynamicDataTypeContext";

const BuilderOrderData = ({
    node,
    builderProps,
    formatCurrency,
    order,
    children,
    disabled,
    layer,
    refundObj
}: OrderDataComponentProps) => {
    const div = useRef<HTMLDivElement>(null);

    const repeaterDivIndex = findInChildren(children, "repeaterDiv");
    const orderOrRefund = refundObj || order;
    const { id, stateManager, attr } = node;

    const { setElement, states } = useInteractionStates();

    const orderDataClass = classNames("order-data-wrapper", {
        [`${attr?.className}`]: !!attr?.className
    });

    const previewLineItems = [
        {
            productId: "1",
            name: "Product 1",
            quantity: 2,
            price: 10,
            total: 30,
            discount: 0,
            totalTax: 0,
            fee: {
                itemFee: {
                    amount: 10
                }
            }
        }
    ] as unknown as LineItem[]; //empty object for preview

    if (builderProps) {
        const { nodeState, selectCallback, outlineCallback, onDragStart, onDragEnd, onDragOverCapture } = builderProps;

        return (
            <DynamicDataSectionProvider
                value={{ dynamicDataScope: ActiveEntityType.ORDER, dynamicDataObject: { order: orderOrRefund } }}
            >
                <div
                    className={orderDataClass}
                    style={getCompoundStyle({
                        node,
                        stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
                    })}
                    ref={div}
                    onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                    onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                    id={id}
                    onDragStart={e => onDragStart?.(e, id)}
                    draggable={"true"}
                    onDragEnd={onDragEnd}
                    onDragOverCapture={e => onDragOverCapture?.(e, id, div.current as HTMLDivElement)}
                >
                    {children &&
                        children.map((child, idx) => {
                            if (idx === repeaterDivIndex) {
                                const lineItemsToShow =
                                    orderOrRefund && (orderOrRefund?.lineItems?.length || orderOrRefund?.customSales?.length)
                                        ? [...(orderOrRefund?.lineItems || []), ...(orderOrRefund?.customSales || [])]
                                        : previewLineItems;

                                return (
                                    <div className="line-items" key={idx}>
                                        {lineItemsToShow.map((lineItem, idx) => (
                                            <SingleLineItem
                                                key={`${
                                                    "productId" in lineItem ? lineItem.productId : lineItem.customSaleId
                                                }${idx} ${(orderOrRefund as ActiveOrder)?._id || ""}`}
                                                lineItem={lineItem}
                                                divElement={child}
                                                idx={idx}
                                                formatCurrency={
                                                    formatCurrency ||
                                                    ((amount?: any) => {
                                                        if (!amount) return "0";
                                                        let amountToFormat = amount;
                                                        if (typeof amountToFormat === "string") {
                                                            amountToFormat = parseFloat(amount);
                                                        }
                                                        return amountToFormat.toFixed(2);
                                                    })
                                                }
                                            />
                                        ))}
                                    </div>
                                );
                            } else {
                                return child;
                            }
                        })}
                </div>
            </DynamicDataSectionProvider>
        );
    } else {
        const wrapperStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: getCompoundStyle({ node }),
            stateManager,
            paths: ["attr_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        return (
            <DynamicDataSectionProvider
                value={{ dynamicDataScope: ActiveEntityType.ORDER, dynamicDataObject: { order: orderOrRefund } }}
            >
                <div className={orderDataClass} style={wrapperStyle} ref={setElement}>
                    {children.map((child, idx) => {
                        if (idx === repeaterDivIndex) {
                            const lineItemsToShow =
                                orderOrRefund && (orderOrRefund?.lineItems?.length || orderOrRefund?.customSales?.length)
                                    ? [...(orderOrRefund?.lineItems || []), ...(orderOrRefund?.customSales || [])]
                                    : [];
                            return lineItemsToShow.length ? (
                                <div className="line-items" key={idx}>
                                    {lineItemsToShow.map((lineItem, idx) => (
                                        <SingleLineItem
                                            key={`${
                                                "productId" in lineItem
                                                    ? lineItem.internalId || lineItem.productId
                                                    : lineItem.customSaleId
                                            }-${idx}-${(orderOrRefund as ActiveOrder)?._id || ""}`}
                                            lineItem={lineItem}
                                            divElement={child}
                                            idx={idx}
                                            renderProps={{ layer: layer!, node: node }}
                                            formatCurrency={
                                                formatCurrency ||
                                                ((amount?: any) => {
                                                    if (!amount) return "0";
                                                    let amountToFormat = amount;
                                                    if (typeof amountToFormat === "string") {
                                                        amountToFormat = parseFloat(amount);
                                                    }
                                                    return amountToFormat.toFixed(2);
                                                })
                                            }
                                        />
                                    ))}
                                </div>
                            ) : null;
                        } else {
                            return child;
                        }
                    })}
                </div>
            </DynamicDataSectionProvider>
        );
    }
};

export default BuilderOrderData;
